// frontend/src/stores/ships.js
import { defineStore } from 'pinia'
import { ref, computed, shallowRef } from 'vue'
import axios from 'axios'
import { useAuthStore } from './auth'

export const useShipsStore = defineStore('ships', () => {
  const authStore = useAuthStore()
  
  // Use shallowRef for large arrays - prevents deep reactivity
  const ships = shallowRef([])
  const selectedShip = ref(null)
  const loading = ref(false)
  const error = ref(null)
  const lastUpdate = ref(null)
  const filters = ref({
    type: 'all',
    search: '',
    minSpeed: 0,
    maxSpeed: 50
  })

  // Use Map for O(1) lookups
  const shipsMap = new Map()
  let updateFrame = null
  let pendingUpdates = []
  
  // Batch update timer
  let batchTimer = null
  let pendingBatch = []
  const BATCH_DELAY_MS = 50

  function toNumberOrNull(value) {
    if (value === null || value === undefined) return null
    const num = Number(value)
    return isNaN(num) ? null : num
  }

  // Get ship type efficiently - pre-computed lookup table
  const shipTypeCache = new Map()
  
  function getShipTypeFromCode(code) {
    if (code === undefined || code === null) return 'unknown'
    
    if (shipTypeCache.has(code)) return shipTypeCache.get(code)
    
    const numCode = typeof code === 'string' ? parseInt(code, 10) : code
    if (isNaN(numCode) || numCode < 0 || numCode > 99) {
      shipTypeCache.set(code, 'unknown')
      return 'unknown'
    }
    
    const firstDigit = Math.floor(numCode / 10)
    let result
    
    switch (firstDigit) {
      case 2: result = 'hsc'; break
      case 3:
        if (numCode === 30) result = 'fishing'
        else if (numCode === 31 || numCode === 32) result = 'tug'
        else if (numCode === 36) result = 'sailing'
        else if (numCode === 37) result = 'pleasure'
        else result = 'unknown'
        break
      case 4: result = 'hsc'; break
      case 5:
        if (numCode === 51) result = 'sar'
        else if (numCode === 52) result = 'tug'
        else if (numCode === 55) result = 'law'
        else if (numCode === 58) result = 'medical'
        else result = 'unknown'
        break
      case 6: result = 'passenger'; break
      case 7: result = 'cargo'; break
      case 8: result = 'tanker'; break
      case 9:
        if (numCode === 99) result = 'aton'
        else result = 'unknown'
        break
      default: result = 'unknown'
    }
    
    shipTypeCache.set(code, result)
    return result
  }

  // Computed with caching for performance
  let cachedSourceFilteredShips = null
  let cachedFilteredShips = null
  let lastSourceFilter = null
  let lastFiltersHash = null
  
  const sourceFilteredShips = computed(() => {
    const userSource = authStore.userSource
    if (userSource === 'all') {
      return ships.value
    }
    return ships.value.filter(ship => ship.source === userSource)
  })

  const filteredShips = computed(() => {
    let result = sourceFilteredShips.value
    
    if (filters.value.type !== 'all') {
      result = result.filter(ship => {
        const type = getShipTypeFromCode(ship.type_code)
        return type === filters.value.type
      })
    }
    
    if (filters.value.search) {
      const searchLower = filters.value.search.toLowerCase()
      result = result.filter(ship =>
        (ship.name?.toLowerCase().includes(searchLower)) ||
        (ship.mmsi?.includes(filters.value.search))
      )
    }
    
    result = result.filter(ship => {
      const sog = ship.sog || 0
      return sog >= filters.value.minSpeed && sog <= filters.value.maxSpeed
    })
    
    return result
  })

  const activeShips = computed(() => {
    const twoHoursAgo = Date.now() - 2 * 60 * 60 * 1000
    return filteredShips.value.filter(s => new Date(s.ts).getTime() > twoHoursAgo)
  })

  // Fetch all ships from API
  async function fetchShips() {
    loading.value = true
    error.value = null
    
    try {
      const params = {}
      if (authStore.userSource !== 'all') {
        params.source = authStore.userSource
      }
      
      const response = await axios.get('/api/ais', {
        params,
        headers: { Authorization: `Bearer ${authStore.token}` },
        timeout: 30000
      })
      
      let shipArray = []
      if (response.data?.data) {
        shipArray = response.data.data
      } else if (Array.isArray(response.data)) {
        shipArray = response.data
      }
      
      // Update Map for fast lookups
      shipsMap.clear()
      for (const ship of shipArray) {
        shipsMap.set(String(ship.mmsi), ship)
      }
      
      ships.value = shipArray
      lastUpdate.value = new Date()
      
    } catch (err) {
      error.value = err.response?.data?.message || 'Failed to fetch ships'
      console.error('Error fetching ships:', err)
    } finally {
      loading.value = false
    }
  }

  async function fetchShipDetails(mmsi) {
    loading.value = true
    error.value = null
    
    try {
      const response = await axios.get(`/api/ais/${mmsi}`, {
        params: { history: true, days: 7 },
        headers: { Authorization: `Bearer ${authStore.token}` },
        timeout: 15000
      })
      
      selectedShip.value = response.data
      return response.data
    } catch (err) {
      error.value = err.response?.data?.message || 'Failed to fetch ship details'
      return null
    } finally {
      loading.value = false
    }
  }

  // Apply batch update from WebSocket
  function applyBatchUpdate(batchUpdates) {
    if (!batchUpdates || !batchUpdates.length) return
    
    // Process immediately for performance
    let needsFullRefresh = false
    let updatedShipData = null
    
    for (const update of batchUpdates) {
      const mmsi = String(update.mmsi)
      const existing = shipsMap.get(mmsi)
      
      if (existing) {
        // Preserve existing static data, update only navigation fields
        let changed = false
        
        if (update.lat !== undefined && update.lat !== existing.lat) {
          existing.lat = toNumberOrNull(update.lat)
          changed = true
        }
        if (update.lng !== undefined && update.lng !== existing.lng) {
          existing.lng = toNumberOrNull(update.lng)
          changed = true
        }
        if (update.sog !== undefined && update.sog !== existing.sog) {
          existing.sog = toNumberOrNull(update.sog)
          changed = true
        }
        if (update.cog !== undefined && update.cog !== existing.cog) {
          existing.cog = toNumberOrNull(update.cog)
          changed = true
        }
        if (update.heading !== undefined && update.heading !== existing.heading) {
          existing.heading = toNumberOrNull(update.heading)
          changed = true
        }
        if (update.rot !== undefined && update.rot !== existing.rot) {
          existing.rot = toNumberOrNull(update.rot)
          changed = true
        }
        if (update.ts !== undefined) {
          existing.ts = update.ts
          changed = true
        }
        
        // Static data changes - trigger full refresh
        if (update.name !== undefined && update.name !== existing.name) {
          existing.name = update.name
          needsFullRefresh = true
        }
        if (update.type_code !== undefined && update.type_code !== existing.type_code) {
          existing.type_code = toNumberOrNull(update.type_code)
          needsFullRefresh = true
          shipTypeCache.delete(existing.type_code)
        }
        if (update.toBow !== undefined && update.toBow !== existing.toBow) {
          existing.toBow = toNumberOrNull(update.toBow)
          needsFullRefresh = true
        }
        if (update.toStern !== undefined && update.toStern !== existing.toStern) {
          existing.toStern = toNumberOrNull(update.toStern)
          needsFullRefresh = true
        }
        if (update.toPortside !== undefined && update.toPortside !== existing.toPortside) {
          existing.toPortside = toNumberOrNull(update.toPortside)
          needsFullRefresh = true
        }
        if (update.toStarboard !== undefined && update.toStarboard !== existing.toStarboard) {
          existing.toStarboard = toNumberOrNull(update.toStarboard)
          needsFullRefresh = true
        }
        if (update.callSign !== undefined && update.callSign !== existing.callSign) {
          existing.callSign = update.callSign
          needsFullRefresh = true
        }
        if (update.dest !== undefined && update.dest !== existing.dest) {
          existing.dest = update.dest
          needsFullRefresh = true
        }
        if (update.draught !== undefined && update.draught !== existing.draught) {
          existing.draught = toNumberOrNull(update.draught)
          needsFullRefresh = true
        }
        
        shipsMap.set(mmsi, existing)
        
        if (changed && !updatedShipData) {
          updatedShipData = existing
        }
      } else {
        // New ship
        const newShip = {
          mmsi: update.mmsi,
          lat: toNumberOrNull(update.lat),
          lng: toNumberOrNull(update.lng),
          sog: toNumberOrNull(update.sog),
          cog: toNumberOrNull(update.cog),
          heading: toNumberOrNull(update.heading),
          rot: toNumberOrNull(update.rot),
          ts: update.ts || new Date().toISOString(),
          name: update.name || null,
          callSign: update.callSign || null,
          type: update.type || null,
          type_code: toNumberOrNull(update.type_code),
          flag: update.flag || null,
          dest: update.dest || null,
          eta: update.eta || null,
          navStat: update.navStat || null,
          navStat_code: toNumberOrNull(update.navStat_code),
          toBow: toNumberOrNull(update.toBow),
          toStern: toNumberOrNull(update.toStern),
          toPortside: toNumberOrNull(update.toPortside),
          toStarboard: toNumberOrNull(update.toStarboard),
          draught: toNumberOrNull(update.draught),
          accuracy: toNumberOrNull(update.accuracy),
          imo: update.imo || null,
          source: update.source || authStore.userSource || 'unknown'
        }
        shipsMap.set(mmsi, newShip)
        needsFullRefresh = true
      }
    }
    
    // Update the reactive array in batch
    if (needsFullRefresh) {
      // Full refresh - rebuild array from Map
      const newShipsArray = Array.from(shipsMap.values())
      ships.value = newShipsArray
    } else if (updatedShipData) {
      // Partial update - find and replace single ship
      const currentShips = ships.value
      const index = currentShips.findIndex(s => String(s.mmsi) === String(updatedShipData.mmsi))
      if (index !== -1) {
        const newShipsArray = [...currentShips]
        newShipsArray[index] = updatedShipData
        ships.value = newShipsArray
      }
    }
    
    lastUpdate.value = new Date()
  }

  // Set initial ships
  function setInitialShips(shipList) {
    if (!shipList || !shipList.length) return
    
    shipsMap.clear()
    for (const ship of shipList) {
      shipsMap.set(String(ship.mmsi), ship)
    }
    
    ships.value = [...shipList]
    lastUpdate.value = new Date()
  }

  // Update single ship navigation
  function updateShipNavigation(mmsi, navigationData) {
    if (!mmsi || !navigationData) return false
    
    const existing = shipsMap.get(String(mmsi))
    if (!existing) return false
    
    let changed = false
    
    if (navigationData.lat !== undefined && navigationData.lat !== existing.lat) {
      existing.lat = toNumberOrNull(navigationData.lat)
      changed = true
    }
    if (navigationData.lng !== undefined && navigationData.lng !== existing.lng) {
      existing.lng = toNumberOrNull(navigationData.lng)
      changed = true
    }
    if (navigationData.cog !== undefined && navigationData.cog !== existing.cog) {
      existing.cog = toNumberOrNull(navigationData.cog)
      changed = true
    }
    if (navigationData.sog !== undefined && navigationData.sog !== existing.sog) {
      existing.sog = toNumberOrNull(navigationData.sog)
      changed = true
    }
    if (navigationData.heading !== undefined && navigationData.heading !== existing.heading) {
      existing.heading = toNumberOrNull(navigationData.heading)
      changed = true
    }
    if (navigationData.rot !== undefined && navigationData.rot !== existing.rot) {
      existing.rot = toNumberOrNull(navigationData.rot)
      changed = true
    }
    if (navigationData.ts !== undefined) {
      existing.ts = navigationData.ts
      changed = true
    }
    
    if (changed) {
      shipsMap.set(String(mmsi), existing)
      const currentShips = ships.value
      const index = currentShips.findIndex(s => String(s.mmsi) === String(mmsi))
      if (index !== -1) {
        const newShipsArray = [...currentShips]
        newShipsArray[index] = existing
        ships.value = newShipsArray
      }
      lastUpdate.value = new Date()
      return true
    }
    
    return false
  }

  function updateShipFromWebSocket(shipData) {
    if (!shipData || !shipData.mmsi) return
    
    const existing = shipsMap.get(String(shipData.mmsi))
    if (existing) {
      Object.assign(existing, shipData)
      shipsMap.set(String(shipData.mmsi), existing)
      
      const currentShips = ships.value
      const index = currentShips.findIndex(s => String(s.mmsi) === String(shipData.mmsi))
      if (index !== -1) {
        const newShipsArray = [...currentShips]
        newShipsArray[index] = existing
        ships.value = newShipsArray
      }
    } else {
      shipsMap.set(String(shipData.mmsi), { ...shipData })
      ships.value = [...ships.value, shipData]
    }
    
    lastUpdate.value = new Date()
  }

  function getShipFromCache(mmsi) {
    return shipsMap.get(String(mmsi))
  }

  function getAllShipsFromCache() {
    return Array.from(shipsMap.values())
  }

  function selectShip(ship) {
    selectedShip.value = ship
  }

  function clearSelection() {
    selectedShip.value = null
  }

  function updateFilters(newFilters) {
    filters.value = { ...filters.value, ...newFilters }
  }

  function resetFilters() {
    filters.value = {
      type: 'all',
      search: '',
      minSpeed: 0,
      maxSpeed: 50
    }
  }

  // Cleanup on unmount
  function cleanup() {
    if (batchTimer) clearTimeout(batchTimer)
    if (updateFrame) cancelAnimationFrame(updateFrame)
    shipsMap.clear()
    shipTypeCache.clear()
  }

  return {
    // State
    ships,
    selectedShip,
    loading,
    error,
    lastUpdate,
    filters,
    
    // Computed
    sourceFilteredShips,
    filteredShips,
    activeShips,
    
    // Methods
    fetchShips,
    fetchShipDetails,
    setInitialShips,
    applyBatchUpdate,
    updateShipFromWebSocket,
    updateShipNavigation,
    getShipFromCache,
    getAllShipsFromCache,
    selectShip,
    clearSelection,
    updateFilters,
    resetFilters,
    getShipTypeFromCode,
    cleanup
  }
})